import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuth } from "../../hooks/useAuth";
import { useDecodableApiFetch } from "../../lib/api";
import { QueryObserverResult, RefetchOptions, useQuery } from "react-query";
import { auth0LoginOptions } from "../../lib/util";
import { useParams } from "react-router-dom";
import { useSnackbar } from "../../components/SnackbarProvider";
import { Account, AccountInformation } from "decodable-sdk-js/v1alpha2";

export const AccountsContext = createContext(null);

export type AccountsContextValue = {
  isLoading: boolean;
  error?: any;
  accounts?: Account[];
  refresh: () => Promise<QueryObserverResult<Account[], any>>;
};

export const ErrUnauthorized = new Error("unauthorized");

export function AccountsProvider({ children }) {
  const { isLoading: authLoading, isAuthenticated } = useAuth();

  const decodableApiFetch = useDecodableApiFetch();

  const {
    isLoading,
    data: accounts,
    error,
    refetch,
  } = useQuery(
    ["accounts"],
    async () => (await decodableApiFetch("/v1alpha2/accounts")).items,
    {
      enabled: !authLoading && isAuthenticated,
      refetchOnWindowFocus: false,
    }
  );

  const value = {
    isLoading,
    error,
    accounts,
    refresh: refetch,
  };

  return (
    <AccountsContext.Provider value={value}>
      <ActiveAccountProvider>{children}</ActiveAccountProvider>
    </AccountsContext.Provider>
  );
}

export function useAccounts(): AccountsContextValue {
  const ctx = useContext(AccountsContext);
  if (ctx == null) {
    throw new Error(
      `useAccounts may only be used in the context of an AccountsProvider!`
    );
  }
  return ctx;
}

export type ActiveAccountContextValue = {
  isLoading: boolean;
  error: any;
  refetchAccountInfo: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<AccountInformation, Error>>;
  activeAccount: Account;
  accountInfo: AccountInformation;
  accountInfoIsLoading: boolean;
  accountInfoError: Error;
};

export const ActiveAccountContext =
  createContext<ActiveAccountContextValue | null>(null);

export function useActiveAccountValue(): ActiveAccountContextValue {
  const {
    isLoading: authLoading,
    isAuthenticated,
    jwt,
    error: authError,
  } = useAuth();
  const decodableApiFetch = useDecodableApiFetch();

  const {
    isLoading: accountsLoading,
    accounts,
    error: accountsError,
  } = useAccounts();

  const isLoading = authLoading || accountsLoading || (isAuthenticated && !jwt);
  const error = authError || accountsError;

  // Find the active account from the accounts list matching the org on the JWT
  const activeAccount = useMemo(() => {
    if (!jwt || !accounts) {
      return;
    }
    const account = accounts?.find(
      (acc: Account) => acc.auth0_org_id === jwt.org_id
    );
    return account;
  }, [accounts, jwt]);

  const showSnackbar = useSnackbar();

  const {
    data: accountInfo,
    isLoading: accountInfoIsLoading,
    error: accountInfoError,
    refetch: refetchAccountInfo,
  } = useQuery<AccountInformation, Error>(
    ["accounts", "info", activeAccount?.name],
    () => {
      let fetchOpts = activeAccount.cell
        ? { targetCell: activeAccount.cell }
        : { accountName: activeAccount.name };
      return decodableApiFetch<AccountInformation>(
        `/v1alpha2/accounts/${activeAccount.name}`,
        {},
        fetchOpts
      );
    },
    {
      enabled: Boolean(activeAccount?.name),
      refetchOnWindowFocus: false,
      onError: (err) => {
        showSnackbar({
          title: `Failed to retrieve account information`,
          message: err.message ?? err.toString(),
          severity: "error",
        });
      },
    }
  );

  return {
    isLoading,
    error,
    refetchAccountInfo,
    activeAccount,
    accountInfo,
    accountInfoIsLoading,
    accountInfoError,
  };
}

export function ActiveAccountProvider({ children }) {
  const ctx = useActiveAccountValue();
  return (
    <ActiveAccountContext.Provider value={ctx}>
      {children}
    </ActiveAccountContext.Provider>
  );
}

export function useActiveAccount() {
  const ctx = useContext(ActiveAccountContext);
  if (ctx == null) {
    throw new Error(
      `useActiveAccount may only be used in the context of an ActiveAccountProvider!`
    );
  }
  return ctx;
}

export function useAccountFromPath() {
  const { accountName: accountNameFromPath } = useParams();

  const {
    isLoading: accountsLoading,
    error: accountsError,
    accounts,
  } = useAccounts();

  const { accountFromPath } = useMemo(() => {
    const accountFromPath = accounts?.find(
      (acct) => acct.name == accountNameFromPath
    );

    return {
      accountFromPath,
    };
  }, [accountNameFromPath, accounts]);

  return {
    accountNameFromPath,
    accountFromPath,
    isValid: !!accountFromPath,
    isLoading: accountsLoading,
    error: accountsError,
  };
}

export type ChangeAccountIntent = {
  target?: string;
  account: Account;
};

export function useAccountLogin() {
  const { isLoading: authIsLoading, loginWithRedirect } = useAuth();
  const [state, setState] = useState({
    intent: null as ChangeAccountIntent,
    error: null,
    isLoading: false,
  });

  useEffect(() => {
    if (authIsLoading || !state.intent) {
      return;
    }

    loginWithRedirect(
      auth0LoginOptions({
        organization: state.intent.account.auth0_org_id,
        target: state.intent.target,
      })
    )
      .then(() => {
        setState((state) => ({
          ...state,
          isLoading: false,
        }));
      })
      .catch((err) => {
        setState((state) => ({
          ...state,
          intent: null,
          error: err,
          isLoading: false,
        }));
      });
  }, [authIsLoading, loginWithRedirect, state.intent]);

  return {
    login: ({
      target = currentLocation(),
      account,
    }: {
      target?: string;
      account: Account;
    }) => {
      setState((state) => ({
        ...state,
        error: null,
        isLoading: true,
        intent: { target, account },
      }));
    },
    ...state,
  };
}

export function useAccountPaths() {
  const { activeAccount } = useActiveAccount();

  return {
    connection(id: string) {
      return `/${activeAccount.name}/connections/${id}`;
    },
    stream(id: string) {
      return `/${activeAccount.name}/streams/${id}`;
    },
    pipeline(id: string, version: number | string = "latest") {
      return `/${activeAccount.name}/pipelines/${id}?version=${version}`;
    },
  };
}

function currentLocation() {
  return window.location.pathname + window.location.search;
}
